import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Search, Play, Check, X, AlertTriangle, ChevronDown, Undo2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { usePortfolioPrefsStore } from '../store';
import { convertAmount, currencyLabel } from '../lib/currencyConvert';
import { formatCurrency } from '../lib/utils';
import { recurringCostCandidatesAPI } from '../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { COST_TYPE_DEFS } from '../lib/costTaxonomy';

const CADENCE_LABELS = { monthly: 'Monthly', quarterly: 'Quarterly', annual: 'Annual' };

const COST_TYPE_OPTIONS = COST_TYPE_DEFS
  .filter((d) => !d.is_percentage)
  .map((d) => ({ value: d.code, label: d.display_label }));

export function DetectedRecurringCosts({ eurGbpRate }) {
  const displayCurrency = usePortfolioPrefsStore((s) => s.displayCurrency);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [entityFilter, setEntityFilter] = useState('all');
  const [showDismissed, setShowDismissed] = useState(false);

  // Promote state
  const [promoteId, setPromoteId] = useState(null);
  const [promoteProperties, setPromoteProperties] = useState([]);
  const [promoteForm, setPromoteForm] = useState(null);
  const [promotePreview, setPromotePreview] = useState(null);
  const [promoteLoading, setPromoteLoading] = useState(false);

  // Dismiss state
  const [dismissId, setDismissId] = useState(null);
  const [dismissReason, setDismissReason] = useState('');
  const [dismissLoading, setDismissLoading] = useState(false);

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      // Property Portfolio Costs page: portfolio outgoings only (Fan, 2026-10-25).
      const params = { scope: 'portfolio' };
      if (entityFilter !== 'all') params.entity_id = entityFilter;
      if (showDismissed) params.include_dismissed = true;
      const { data } = await recurringCostCandidatesAPI.list(params);
      setCandidates(data.candidates || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [entityFilter, showDismissed]);

  useEffect(() => { fetchCandidates(); }, [fetchCandidates]);

  const runDetection = async () => {
    setDetecting(true);
    try {
      await recurringCostCandidatesAPI.runDetection();
      toast.success('Detection complete');
      await fetchCandidates();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDetecting(false);
    }
  };

  // Promote flow
  const openPromote = async (candidate) => {
    setPromoteId(candidate.id);
    setPromotePreview(null);
    setPromoteForm({
      property_id: '',
      cost_type: 'insurance',
      amount: Math.abs(parseFloat(candidate.representative_amount || 0)),
      currency: candidate.currency || 'GBP',
      frequency: candidate.cadence || 'monthly',
      effective_from: candidate.first_seen ? candidate.first_seen.slice(0, 10) : '',
      notes: '',
      mortgage_interest: '',
      mortgage_principal: '',
    });
    try {
      const { data } = await recurringCostCandidatesAPI.getProperties(candidate.id);
      setPromoteProperties(data.properties || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const isMortgageType = (ct) => ct === 'mortgage_interest' || ct === 'mortgage_principal';

  const doPreview = async () => {
    if (!promoteForm || !promoteId) return;
    setPromoteLoading(true);
    try {
      const payload = {
        property_id: promoteForm.property_id,
        cost_type: promoteForm.cost_type,
        amount: promoteForm.amount,
        currency: promoteForm.currency,
        frequency: promoteForm.frequency,
        effective_from: promoteForm.effective_from,
        notes: promoteForm.notes || undefined,
      };
      if (isMortgageType(promoteForm.cost_type)) {
        payload.mortgage_interest = parseFloat(promoteForm.mortgage_interest) || 0;
        payload.mortgage_principal = parseFloat(promoteForm.mortgage_principal) || 0;
      }
      const { data } = await recurringCostCandidatesAPI.preview(promoteId, payload);
      setPromotePreview(data.preview);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPromoteLoading(false);
    }
  };

  const doPromote = async () => {
    if (!promoteForm || !promoteId) return;
    setPromoteLoading(true);
    try {
      const payload = {
        property_id: promoteForm.property_id,
        cost_type: promoteForm.cost_type,
        amount: promoteForm.amount,
        currency: promoteForm.currency,
        frequency: promoteForm.frequency,
        effective_from: promoteForm.effective_from,
        notes: promoteForm.notes || undefined,
      };
      if (isMortgageType(promoteForm.cost_type)) {
        payload.mortgage_interest = parseFloat(promoteForm.mortgage_interest) || 0;
        payload.mortgage_principal = parseFloat(promoteForm.mortgage_principal) || 0;
      }
      await recurringCostCandidatesAPI.promote(promoteId, payload);
      toast.success('Cost promoted successfully');
      setPromoteId(null);
      setPromoteForm(null);
      setPromotePreview(null);
      await fetchCandidates();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPromoteLoading(false);
    }
  };

  const doDismiss = async () => {
    if (!dismissId || !dismissReason.trim()) return;
    setDismissLoading(true);
    try {
      await recurringCostCandidatesAPI.dismiss(dismissId, { reason: dismissReason.trim() });
      toast.success('Candidate dismissed');
      setDismissId(null);
      setDismissReason('');
      await fetchCandidates();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDismissLoading(false);
    }
  };

  const doUndoPromote = async (candidateId) => {
    try {
      await recurringCostCandidatesAPI.undoPromote(candidateId);
      toast.success('Promotion undone');
      await fetchCandidates();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const cv = (val, fromCurrency) => convertAmount(val, fromCurrency, displayCurrency, eurGbpRate);

  // Derive entity list for filter
  const entities = [...new Map(candidates.map((c) => [c.entity_id, c.entity_name || c.entity_id])).entries()]
    .sort((a, b) => a[1].localeCompare(b[1]));

  // Group candidates by entity
  const grouped = {};
  for (const c of candidates) {
    const eid = c.entity_id;
    if (!grouped[eid]) grouped[eid] = { name: c.entity_name || eid, candidates: [] };
    grouped[eid].candidates.push(c);
  }

  const activeCount = candidates.filter((c) => c.status === 'new' && !c.is_lapsed).length;
  const lapsedCount = candidates.filter((c) => c.status === 'new' && c.is_lapsed).length;
  const promotedCount = candidates.filter((c) => c.status === 'promoted').length;
  const dismissedCount = candidates.filter((c) => c.status === 'dismissed').length;

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {activeCount} active, {lapsedCount} lapsed, {promotedCount} promoted
            {showDismissed && `, ${dismissedCount} dismissed`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Entity filter */}
          <select
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            data-testid="detected-entity-filter"
          >
            <option value="all">All entities</option>
            {entities.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>

          {/* Show dismissed toggle */}
          <label className="flex items-center gap-1.5 text-sm text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={showDismissed}
              onChange={(e) => setShowDismissed(e.target.checked)}
              className="rounded"
              data-testid="show-dismissed-toggle"
            />
            Show dismissed
          </label>

          {/* Run detection button */}
          <button
            onClick={runDetection}
            disabled={detecting}
            className="inline-flex items-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50"
            data-testid="run-detection-btn"
          >
            {detecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            Run Detection Now
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : candidates.length === 0 ? (
        <Card>
          <CardContent className="py-8">
            <p className="text-center text-muted-foreground text-sm">
              No recurring cost candidates detected. Click "Run Detection Now" to scan bank transactions.
            </p>
          </CardContent>
        </Card>
      ) : (
        Object.entries(grouped).sort(([, a], [, b]) => a.name.localeCompare(b.name)).map(([eid, group]) => (
          <Card key={eid}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">{group.name}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[220px]">Label</TableHead>
                    <TableHead>Cadence</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Spread</TableHead>
                    <TableHead>Occurrences</TableHead>
                    <TableHead>First / Last</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {group.candidates.map((c) => {
                    const isInactive = c.status === 'promoted' || c.status === 'dismissed';
                    const rowCls = isInactive ? 'opacity-50' : '';
                    const amt = Math.abs(parseFloat(c.representative_amount || 0));
                    const spread = c.amount_spread_pct != null ? `${(c.amount_spread_pct * 100).toFixed(1)}%` : '-';

                    return (
                      <React.Fragment key={c.id}>
                        <TableRow className={rowCls} data-testid={`candidate-row-${c.id}`}>
                          <TableCell className="font-mono text-xs max-w-[220px] truncate" title={c.sample_raw_labels?.length ? c.sample_raw_labels.join('\n') : c.normalized_label}>
                            {c.normalized_label}
                            {c.is_lapsed && (
                              <Badge variant="outline" className="ml-1 text-[10px] border-orange-300 text-orange-600">LAPSED</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-xs">{CADENCE_LABELS[c.cadence] || c.cadence}</TableCell>
                          <TableCell className="text-right font-mono text-xs tabular-nums">
                            {formatCurrency(cv(amt, c.currency), displayCurrency)}
                          </TableCell>
                          <TableCell className="text-right text-xs text-muted-foreground">{spread}</TableCell>
                          <TableCell className="text-xs">{c.occurrence_count}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {c.first_seen ? new Date(c.first_seen).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }) : '-'}
                            {' / '}
                            {c.last_seen ? new Date(c.last_seen).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }) : '-'}
                          </TableCell>
                          <TableCell>
                            {c.status === 'promoted' && (
                              <Badge className="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 text-[10px]">Promoted</Badge>
                            )}
                            {c.status === 'dismissed' && (
                              <span className="text-xs text-muted-foreground" title={c.dismissal_reason}>
                                Dismissed: {c.dismissal_reason}
                              </span>
                            )}
                            {c.status === 'new' && (
                              <Badge variant="outline" className="text-[10px]">New</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {c.status === 'new' && (
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => openPromote(c)}
                                  className="inline-flex items-center gap-1 h-7 px-2 text-xs font-medium rounded border border-green-300 text-green-700 hover:bg-green-50 dark:border-green-700 dark:text-green-400 dark:hover:bg-green-950/30"
                                  data-testid={`promote-btn-${c.id}`}
                                >
                                  <Check className="h-3 w-3" /> Promote
                                </button>
                                <button
                                  onClick={() => { setDismissId(c.id); setDismissReason(''); }}
                                  className="inline-flex items-center gap-1 h-7 px-2 text-xs font-medium rounded border border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-950/30"
                                  data-testid={`dismiss-btn-${c.id}`}
                                >
                                  <X className="h-3 w-3" /> Dismiss
                                </button>
                              </div>
                            )}
                            {c.status === 'promoted' && (
                              <button
                                onClick={() => doUndoPromote(c.id)}
                                className="inline-flex items-center gap-1 h-7 px-2 text-xs font-medium rounded border border-orange-300 text-orange-700 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-400 dark:hover:bg-orange-950/30"
                                data-testid={`undo-btn-${c.id}`}
                              >
                                <Undo2 className="h-3 w-3" /> Undo
                              </button>
                            )}
                          </TableCell>
                        </TableRow>

                        {/* Promote form inline */}
                        {promoteId === c.id && promoteForm && (
                          <TableRow data-testid="promote-form-row">
                            <TableCell colSpan={8} className="bg-muted/30 p-4">
                              <div className="space-y-3 max-w-2xl">
                                <h4 className="text-sm font-semibold">Promote to recurring cost</h4>
                                <div className="grid grid-cols-2 gap-3">
                                  {/* Property dropdown (optional for entity-level costs) */}
                                  <div>
                                    <label className="text-xs text-muted-foreground">
                                      Target property {promoteProperties.length === 0 && '(entity-level)'}
                                    </label>
                                    {promoteProperties.length > 0 ? (
                                      <select
                                        value={promoteForm.property_id}
                                        onChange={(e) => setPromoteForm({ ...promoteForm, property_id: e.target.value })}
                                        className="w-full h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm"
                                        data-testid="promote-property-select"
                                      >
                                        <option value="">Select property...</option>
                                        {promoteProperties.map((p) => (
                                          <option key={p.property_id} value={p.property_id}>{p.name}</option>
                                        ))}
                                      </select>
                                    ) : (
                                      <div className="w-full h-9 mt-1 rounded-md border border-input bg-muted/50 px-2 text-sm flex items-center text-muted-foreground" data-testid="promote-entity-level-badge">
                                        Entity-level cost (no property)
                                      </div>
                                    )}
                                  </div>
                                  {/* Cost type */}
                                  <div>
                                    <label className="text-xs text-muted-foreground">Cost type</label>
                                    <select
                                      value={promoteForm.cost_type}
                                      onChange={(e) => setPromoteForm({ ...promoteForm, cost_type: e.target.value })}
                                      className="w-full h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm"
                                      data-testid="promote-cost-type-select"
                                    >
                                      {COST_TYPE_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                      ))}
                                    </select>
                                  </div>
                                  {/* Amount */}
                                  <div>
                                    <label className="text-xs text-muted-foreground">Amount ({promoteForm.currency})</label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={promoteForm.amount}
                                      onChange={(e) => setPromoteForm({ ...promoteForm, amount: parseFloat(e.target.value) || 0 })}
                                      className="w-full h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm"
                                    />
                                  </div>
                                  {/* Frequency */}
                                  <div>
                                    <label className="text-xs text-muted-foreground">Frequency</label>
                                    <select
                                      value={promoteForm.frequency}
                                      onChange={(e) => setPromoteForm({ ...promoteForm, frequency: e.target.value })}
                                      className="w-full h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm"
                                    >
                                      <option value="monthly">Monthly</option>
                                      <option value="quarterly">Quarterly</option>
                                      <option value="annual">Annual</option>
                                    </select>
                                  </div>
                                  {/* Effective from */}
                                  <div>
                                    <label className="text-xs text-muted-foreground">Effective from</label>
                                    <input
                                      type="date"
                                      value={promoteForm.effective_from}
                                      onChange={(e) => setPromoteForm({ ...promoteForm, effective_from: e.target.value })}
                                      className="w-full h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm"
                                    />
                                  </div>
                                  {/* Notes */}
                                  <div>
                                    <label className="text-xs text-muted-foreground">Notes</label>
                                    <input
                                      type="text"
                                      value={promoteForm.notes}
                                      onChange={(e) => setPromoteForm({ ...promoteForm, notes: e.target.value })}
                                      className="w-full h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm"
                                      placeholder="Optional"
                                    />
                                  </div>
                                </div>

                                {/* Mortgage split (conditional) */}
                                {isMortgageType(promoteForm.cost_type) && (
                                  <div className="border-t pt-3 mt-2">
                                    <div className="flex items-center gap-1 mb-2">
                                      <AlertTriangle className="h-3.5 w-3.5 text-orange-500" />
                                      <span className="text-xs text-orange-600 font-medium">
                                        Interest/principal split required (cannot be derived from the bank debit)
                                      </span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                      <div>
                                        <label className="text-xs text-muted-foreground">Interest (P&L eligible)</label>
                                        <input
                                          type="number"
                                          step="0.01"
                                          value={promoteForm.mortgage_interest}
                                          onChange={(e) => setPromoteForm({ ...promoteForm, mortgage_interest: e.target.value })}
                                          className="w-full h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm"
                                          data-testid="promote-mortgage-interest"
                                        />
                                      </div>
                                      <div>
                                        <label className="text-xs text-muted-foreground">Principal (cash-only)</label>
                                        <input
                                          type="number"
                                          step="0.01"
                                          value={promoteForm.mortgage_principal}
                                          onChange={(e) => setPromoteForm({ ...promoteForm, mortgage_principal: e.target.value })}
                                          className="w-full h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm"
                                          data-testid="promote-mortgage-principal"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Preview section */}
                                {promotePreview && (
                                  <div className="border-t pt-3 mt-2">
                                    <h5 className="text-xs font-semibold text-muted-foreground mb-1">Preview: record to be written</h5>
                                    <pre className="text-xs bg-muted p-2 rounded overflow-x-auto" data-testid="promote-preview">
                                      {JSON.stringify(promotePreview, null, 2)}
                                    </pre>
                                  </div>
                                )}

                                {/* Action buttons */}
                                <div className="flex items-center gap-2 pt-1">
                                  <button
                                    onClick={doPreview}
                                    disabled={(promoteProperties.length > 0 && !promoteForm.property_id) || promoteLoading}
                                    className="h-8 px-3 text-xs font-medium rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50"
                                    data-testid="promote-preview-btn"
                                  >
                                    {promoteLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Preview'}
                                  </button>
                                  <button
                                    onClick={doPromote}
                                    disabled={(promoteProperties.length > 0 && !promoteForm.property_id) || !promotePreview || promoteLoading}
                                    className="h-8 px-3 text-xs font-medium rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                                    data-testid="promote-apply-btn"
                                  >
                                    {promoteLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Apply'}
                                  </button>
                                  <button
                                    onClick={() => { setPromoteId(null); setPromoteForm(null); setPromotePreview(null); }}
                                    className="h-8 px-3 text-xs font-medium rounded-md border border-input bg-background hover:bg-muted"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Dismiss form inline */}
                        {dismissId === c.id && (
                          <TableRow data-testid="dismiss-form-row">
                            <TableCell colSpan={8} className="bg-muted/30 p-4">
                              <div className="flex items-center gap-2 max-w-lg">
                                <input
                                  type="text"
                                  placeholder="Reason (required)"
                                  value={dismissReason}
                                  onChange={(e) => setDismissReason(e.target.value)}
                                  className="flex-1 h-8 rounded-md border border-input bg-background px-2 text-sm"
                                  data-testid="dismiss-reason-input"
                                />
                                <button
                                  onClick={doDismiss}
                                  disabled={!dismissReason.trim() || dismissLoading}
                                  className="h-8 px-3 text-xs font-medium rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                                  data-testid="dismiss-confirm-btn"
                                >
                                  {dismissLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Confirm'}
                                </button>
                                <button
                                  onClick={() => { setDismissId(null); setDismissReason(''); }}
                                  className="h-8 px-3 text-xs font-medium rounded-md border border-input bg-background hover:bg-muted"
                                >
                                  Cancel
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
